import React, {useLayoutEffect, useRef, useState} from 'react'
import {
  Box,
  Button,
  FormHelperText,
  Menu,
  MenuItem,
  Stack,
  Typography
} from '@mui/material'
import {Add as AddIcon} from '@mui/icons-material'
import {
  DYNAMIC_TAXONOMY_MARKER,
  LEGACY_DYNAMIC_TAXONOMY_MARKER,
  TAXONOMY_BUCKETS_MARKER,
  TAXONOMY_BUCKETS_WITH_EMOTIONS_MARKER
} from '../../types/visual-semiotics'

interface PromptTemplateEditorProps {
  value: string
  onChange: (value: string) => void
  minRows?: number
  readOnly?: boolean
}

const VARIABLES = [
  {
    token: DYNAMIC_TAXONOMY_MARKER,
    label: 'Organization emotion list',
    description: 'All emotion names in the organization taxonomy'
  },
  {
    token: TAXONOMY_BUCKETS_MARKER,
    label: 'Organization bucket list',
    description: 'All bucket names in the organization taxonomy'
  },
  {
    token: TAXONOMY_BUCKETS_WITH_EMOTIONS_MARKER,
    label: 'Organization buckets with emotions',
    description: 'Every bucket followed by its assigned emotions'
  }
]

const TOKEN_LABELS = new Map<string, string>([
  [DYNAMIC_TAXONOMY_MARKER, 'Organization emotion list'],
  [TAXONOMY_BUCKETS_MARKER, 'Organization bucket list'],
  [TAXONOMY_BUCKETS_WITH_EMOTIONS_MARKER, 'Organization buckets with emotions'],
  [LEGACY_DYNAMIC_TAXONOMY_MARKER, 'Organization emotion instructions']
])

const SUPPORTED_TOKENS = [...TOKEN_LABELS.keys()]

function createVariablePill(token: string): HTMLSpanElement {
  const pill = document.createElement('span')
  pill.dataset.templateVariable = token
  pill.contentEditable = 'false'
  pill.className = 'prompt-template-variable'
  pill.setAttribute('role', 'note')
  pill.setAttribute('aria-label', TOKEN_LABELS.get(token) || 'Prompt variable')
  pill.textContent = TOKEN_LABELS.get(token) || token
  return pill
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || ''
  if (!(node instanceof HTMLElement)) return ''
  const token = node.dataset.templateVariable
  if (token) return token
  if (node.tagName === 'BR') return '\n'
  return Array.from(node.childNodes).map(serializeNode).join('')
}

function containsNode(root: HTMLElement, node: Node | null): boolean {
  return !!node && (node === root || root.contains(node))
}

const PromptTemplateEditor: React.FC<PromptTemplateEditorProps> = ({
  value,
  onChange,
  minRows = 12,
  readOnly = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const selectionRef = useRef<Range | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const serializeEditor = () => editorRef.current
    ? Array.from(editorRef.current.childNodes).map(serializeNode).join('')
    : ''

  const renderValue = () => {
    const editor = editorRef.current
    if (!editor) return
    editor.replaceChildren()
    let remaining = value
    while (remaining.length > 0) {
      const matches = SUPPORTED_TOKENS
        .map(token => ({token, index: remaining.indexOf(token)}))
        .filter(match => match.index >= 0)
        .sort((left, right) => left.index - right.index)
      const next = matches[0]
      if (!next) {
        editor.append(document.createTextNode(remaining))
        break
      }
      if (next.index > 0) editor.append(document.createTextNode(remaining.slice(0, next.index)))
      editor.append(createVariablePill(next.token))
      remaining = remaining.slice(next.index + next.token.length)
    }
    selectionRef.current = null
  }

  useLayoutEffect(() => {
    if (serializeEditor() !== value) renderValue()
  }, [value])

  const rememberSelection = () => {
    const editor = editorRef.current
    const selection = window.getSelection()
    if (!editor || !selection || selection.rangeCount === 0) return
    const range = selection.getRangeAt(0)
    if (containsNode(editor, range.commonAncestorContainer)) {
      selectionRef.current = range.cloneRange()
    }
  }

  const emitChange = () => {
    const next = serializeEditor()
    if (next !== value) onChange(next)
    rememberSelection()
  }

  const insertionRange = (editor: HTMLElement): Range => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const activeRange = selection.getRangeAt(0)
      if (containsNode(editor, activeRange.commonAncestorContainer)) {
        return activeRange.cloneRange()
      }
    }
    if (selectionRef.current && containsNode(editor, selectionRef.current.commonAncestorContainer)) {
      return selectionRef.current.cloneRange()
    }
    const range = document.createRange()
    range.selectNodeContents(editor)
    range.collapse(false)
    return range
  }

  const insertNodeAtSelection = (node: Node) => {
    const editor = editorRef.current
    if (!editor) return
    const range = insertionRange(editor)
    editor.focus()
    const selection = window.getSelection()
    range.deleteContents()
    range.insertNode(node)
    range.setStartAfter(node)
    range.collapse(true)
    selection?.removeAllRanges()
    selection?.addRange(range)
    selectionRef.current = range.cloneRange()
    emitChange()
  }

  const insertTextAtSelection = (text: string) => {
    insertNodeAtSelection(document.createTextNode(text))
  }

  const insertVariableAtSelection = (token: string) => {
    insertNodeAtSelection(createVariablePill(token))
  }

  return (
    <Stack spacing={0.75}>
      {!readOnly && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Prompt content</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onMouseDown={event => {
              rememberSelection()
              event.preventDefault()
            }}
            onClick={event => setMenuAnchor(event.currentTarget)}
          >
            Insert variable
          </Button>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            {VARIABLES.map(variable => (
              <MenuItem
                key={variable.token}
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  setMenuAnchor(null)
                  insertVariableAtSelection(variable.token)
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>{variable.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{variable.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      )}
      <Box
        ref={editorRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        aria-label="Prompt content"
        aria-multiline="true"
        onInput={emitChange}
        onMouseUp={rememberSelection}
        onKeyUp={rememberSelection}
        onFocus={rememberSelection}
        onBlur={rememberSelection}
        onKeyDown={event => {
          if (readOnly || event.key !== 'Enter') return
          event.preventDefault()
          insertTextAtSelection('\n')
        }}
        onPaste={event => {
          if (readOnly) return
          event.preventDefault()
          insertTextAtSelection(event.clipboardData.getData('text/plain').replace(/\r\n/g, '\n'))
        }}
        sx={{
          minHeight: `${minRows * 1.5}em`,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: readOnly ? 'action.hover' : 'background.paper',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          outline: 'none',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          '&:focus': readOnly ? undefined : {
            borderColor: 'primary.main',
            boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`
          },
          '& .prompt-template-variable': {
            display: 'inline-flex',
            alignItems: 'center',
            mx: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 4,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
            fontFamily: 'inherit',
            fontSize: '0.8125rem',
            fontWeight: 600,
            userSelect: 'all'
          }
        }}
      />
      {!readOnly && (
        <FormHelperText>
          Insert organization variables anywhere in the prompt. Each variable can be used more than once.
        </FormHelperText>
      )}
    </Stack>
  )
}

export default PromptTemplateEditor
